import { UserProfile } from '@features/users';
import { Box, Link, Skeleton, Typography } from '@mui/material';
import LoadingPlaceholder from '@shared/components/ui/LoadingPlaceHolder';
import OverflowTooltip from '@shared/components/ui/OverflowTooltip';
import { convertToTranslationKey, dateFormatting, toTitleCase } from '@shared/utils/utilsOld';
import { Defect, JiraIssue } from '../types';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { selectGetDefectStatus } from '../store/selectors';
import { RequestStatusEnum } from '@shared/types';
import { editDefectRequest } from '../store';
import DefectJiraIssueLinkDialog from './DefectJiraIssueLinkDialog';
import UtmsChip from '@shared/components/ui/UtmsChip';
import { DEFECT_TYPE_COLOR } from '../constants';

interface DefectCardInfoProps {
    defect: Defect | null;
}

const DefectCardInfo: FC<DefectCardInfoProps> = ({ defect }) => {
    const { t } = useTranslation();
    const getDefectStatus = useSelector(selectGetDefectStatus);
    const isDefectLoading = getDefectStatus === RequestStatusEnum.Loading;
    const isDefectLoaded = getDefectStatus === RequestStatusEnum.Success;
    const { jiraIssueKey, jiraIssueUrl } = defect ?? {};
    const dispatch = useDispatch();
    const [openJiraDialog, setOpenJiraDialog] = useState(false);

    const handleSaveJiraIssue = (issue: JiraIssue) => {
        if (defect) {
            dispatch(
                editDefectRequest({
                    pk: defect.pk,
                    jiraIssue: issue,
                }),
            );
        }
    };

    const handleOpenJiraDialog = () => {
        setOpenJiraDialog(true);
    };

    const handleCloseJiraDialog = () => {
        setOpenJiraDialog(false);
    };

    return (
        <>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
                <LoadingPlaceholder
                    isLoading={isDefectLoading}
                    skeleton={
                        <>
                            <Skeleton width={70} animation="wave" />
                            <Skeleton width={129} animation="wave" />
                        </>
                    }
                >
                    {isDefectLoaded && (
                        <>
                            <Typography component="span" sx={{ color: 'text.secondary', textWrap: 'nowrap' }}>
                                {t('jira_issue')}:
                            </Typography>
                            {jiraIssueKey ? (
                                <OverflowTooltip title={jiraIssueKey}>
                                    <Link
                                        component={'a'}
                                        underline="hover"
                                        sx={{ cursor: 'pointer' }}
                                        href={jiraIssueUrl}
                                        target="_blank"
                                    >
                                        {jiraIssueKey}
                                    </Link>
                                </OverflowTooltip>
                            ) : (
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    {t('no_issue')}
                                    <Link underline="hover" sx={{ cursor: 'pointer' }} onClick={handleOpenJiraDialog}>
                                        {t('add')}
                                    </Link>
                                </Box>
                            )}
                        </>
                    )}
                </LoadingPlaceholder>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <LoadingPlaceholder
                    isLoading={isDefectLoading}
                    skeleton={
                        <>
                            <Skeleton width={72} animation="wave" />
                            <Skeleton animation="wave" variant="circular" width={25} height={25} />
                            <Skeleton width={81} animation="wave" />
                        </>
                    }
                >
                    {isDefectLoaded && (
                        <>
                            <Typography component="span" sx={{ color: 'text.secondary' }}>
                                {t('updated_by')}:
                            </Typography>
                            <UserProfile
                                popoverInfo={{
                                    avatar: true,
                                    email: true,
                                    profileLink: true,
                                    rocket: true,
                                    role: true,
                                }}
                                popoverTrigger="hover"
                                editedUserId={Number(defect?.lastUpdateByUser)}
                            />
                        </>
                    )}
                </LoadingPlaceholder>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
                <LoadingPlaceholder
                    isLoading={isDefectLoading}
                    skeleton={
                        <>
                            <Skeleton animation="wave" width={108} />
                            <Skeleton animation="wave" width={48} />
                        </>
                    }
                >
                    {isDefectLoaded && (
                        <>
                            <Typography component="span" sx={{ color: 'text.secondary' }}>
                                {t('updated_at')}:
                            </Typography>
                            {dateFormatting(defect?.updatedAt, 'DD.MM.YY')}
                        </>
                    )}
                </LoadingPlaceholder>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <LoadingPlaceholder
                    isLoading={isDefectLoading}
                    skeleton={
                        <>
                            <Skeleton animation="wave" width={108} />
                            <Skeleton animation="wave" width={48} />
                        </>
                    }
                >
                    {isDefectLoaded && defect && (
                        <>
                            <Typography component="span" sx={{ color: 'text.secondary' }}>
                                {t('type')}:
                            </Typography>
                            <UtmsChip
                                label={toTitleCase(t(convertToTranslationKey(defect.type)))}
                                color={DEFECT_TYPE_COLOR[defect.type]}
                            />
                        </>
                    )}
                </LoadingPlaceholder>
            </Box>
            <DefectJiraIssueLinkDialog
                open={openJiraDialog}
                onClose={handleCloseJiraDialog}
                onSave={handleSaveJiraIssue}
            />
        </>
    );
};

export default DefectCardInfo;
